'use client'

import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { LuClipboard, LuCheck } from 'react-icons/lu'
import { cn } from '@/core/utils/common'
import { copyToClipboard } from '@/core/utils/clipboard'

type CopyToClipboardProps = {
  text: string
  children?: ReactNode
  className?: string
  onCopy?: () => void
}

export default function CopyToClipboard({
  text,
  children,
  className,
  onCopy,
}: CopyToClipboardProps) {
  const [copiedText, setCopiedText] = useState<string | null>(null)
  const copied = copiedText === text
  const attempt = useRef(0)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      attempt.current += 1
      clearTimeout(resetTimer.current)
    },
    []
  )

  const handleCopy = useCallback(async () => {
    const currentAttempt = ++attempt.current
    const ok = await copyToClipboard(text)
    if (!ok || currentAttempt !== attempt.current) return
    clearTimeout(resetTimer.current)
    setCopiedText(text)
    resetTimer.current = setTimeout(() => setCopiedText(null), 2000)
    onCopy?.()
  }, [text, onCopy])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex min-h-6 cursor-pointer items-center gap-1.5 text-c13 text-description',
        'transition-colors hover:text-foreground focus:outline-none focus-visible:underline',
        className
      )}
    >
      {children || text}
      {copied ? (
        <LuCheck aria-hidden="true" className="w-4 h-4 text-success" />
      ) : (
        <LuClipboard aria-hidden="true" className="w-4 h-4" />
      )}
      <span role="status" className="sr-only">
        {copied ? '복사했습니다.' : ''}
      </span>
    </button>
  )
}
